import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  UserX,
  UserCheck,
  Users,
  AlertCircle,
  Search,
} from 'lucide-react';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';

/**
 * EMZI NEXUS — Org Adoption: Users by Team (full page)
 *
 * Mode-aware view that groups users by team for one of three lists:
 *   - mode="inactive" → users idle ≥7 days or never signed in
 *   - mode="wau"      → weekly active users (last 7 days)
 *   - mode="mau"      → monthly active users (last 30 days)
 *
 * Each team is rendered as a collapsible card with a severity bar.
 */

const MODES = {
  inactive: {
    field: 'top_inactive_users',
    title: 'Inactive Users by Team',
    subtitleSuffix: '≥7 days inactive or never signed in',
    headerBg: '#7F1D1D',
    icon: UserX,
    barColors: (pct) => {
      if (pct >= 75) return { bar: '#DC2626', tint: '#FEE2E2', label: '#991B1B' };
      if (pct >= 50) return { bar: '#F97316', tint: '#FFEDD5', label: '#9A3412' };
      return { bar: '#F59E0B', tint: '#FEF3C7', label: '#92400E' };
    },
    badgeTone: 'inactive',
    backTo: '/admin/org-adoption',
  },
  wau: {
    field: 'wau_users',
    title: 'Weekly Active Users',
    subtitleSuffix: 'active in the last 7 days',
    headerBg: '#047857',
    icon: UserCheck,
    barColors: (pct) => {
      if (pct >= 75) return { bar: '#059669', tint: '#D1FAE5', label: '#065F46' };
      if (pct >= 50) return { bar: '#10B981', tint: '#D1FAE5', label: '#065F46' };
      return { bar: '#34D399', tint: '#ECFDF5', label: '#065F46' };
    },
    badgeTone: 'active',
    backTo: '/admin/org-adoption',
  },
  mau: {
    field: 'mau_users',
    title: 'Monthly Active Users',
    subtitleSuffix: 'active in the last 30 days',
    headerBg: '#1E3A8A',
    icon: Users,
    barColors: (pct) => {
      if (pct >= 75) return { bar: '#1D4ED8', tint: '#DBEAFE', label: '#1E3A8A' };
      if (pct >= 50) return { bar: '#3B82F6', tint: '#DBEAFE', label: '#1E3A8A' };
      return { bar: '#60A5FA', tint: '#EFF6FF', label: '#1E3A8A' };
    },
    badgeTone: 'active',
    backTo: '/admin/org-adoption',
  },
};

const DaysBadge = ({ days, tone = 'inactive' }) => {
  // For active modes, days = how recently the user signed in.
  // For inactive mode, days = how long they've been gone (or NEVER).
  let bg = '#FEF3C7';
  let fg = '#92400E';
  let label;

  if (tone === 'active') {
    if (days === null || days === undefined) {
      bg = '#E5E7EB';
      fg = '#374151';
      label = '—';
    } else if (days === 0) {
      bg = '#D1FAE5';
      fg = '#065F46';
      label = 'today';
    } else if (days === 1) {
      bg = '#D1FAE5';
      fg = '#065F46';
      label = '1d ago';
    } else {
      bg = '#ECFDF5';
      fg = '#065F46';
      label = `${days}d ago`;
    }
  } else {
    // inactive tone
    label = `${days}d`;
    if (days === null || days === undefined) {
      bg = '#DC2626';
      fg = '#FFFFFF';
      label = 'NEVER';
    } else if (days >= 20) {
      bg = '#FECACA';
      fg = '#991B1B';
    } else if (days >= 15) {
      bg = '#FED7E2';
      fg = '#9F1239';
    }
  }
  return (
    <span
      style={{
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const TeamSection = ({ team, members, totalMembers, defaultOpen, tone, getSeverity }) => {
  const [open, setOpen] = useState(defaultOpen);
  const navigate = useNavigate();

  const memberCount = members.length;
  const totalForPct = totalMembers || memberCount;
  const pct = totalForPct > 0 ? Math.round((memberCount / totalForPct) * 100) : 0;
  const severity = getSeverity(pct);
  const noun = tone === 'active' ? 'active' : 'inactive';

  return (
    <div
      data-testid={`team-section-${team}`}
      style={{
        background: '#fff',
        border: `1px solid ${EMZI_COLORS.border}`,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: EMZI_SHADOWS.sm,
      }}
    >
      <button
        data-testid={`team-toggle-${team}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: '#fff',
          border: 'none',
          padding: '14px 16px 10px',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                color: EMZI_COLORS.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {team}
            </div>
            <div style={{ fontSize: 11, color: EMZI_COLORS.textSecondary, marginTop: 2 }}>
              {memberCount} {noun}
              {totalMembers ? ` of ${totalMembers}` : ''} member{memberCount === 1 ? '' : 's'}
            </div>
          </div>
          <span
            style={{
              background: severity.tint,
              color: severity.label,
              fontSize: 12,
              fontWeight: 800,
              padding: '4px 10px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}
          >
            {pct}%
          </span>
          <ChevronDown
            size={18}
            color={EMZI_COLORS.textMuted}
            style={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          />
        </div>

        {/* Severity bar */}
        <div
          style={{
            background: '#F1F5F9',
            borderRadius: 999,
            height: 6,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.min(100, pct)}%`,
              background: severity.bar,
              height: '100%',
              transition: 'width 0.3s',
            }}
          />
        </div>
      </button>

      {open && (
        <div
          data-testid={`team-members-${team}`}
          style={{
            background: '#F9FAFB',
            borderTop: `1px solid ${EMZI_COLORS.border}`,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {members.map((u) => (
            <button
              key={u.user_id}
              data-testid={`inactive-row-${u.user_id}`}
              onClick={() => navigate(`/admin/users?focus=${encodeURIComponent(u.user_id)}`)}
              style={{
                background: '#fff',
                border: `1px solid ${EMZI_COLORS.border}`,
                borderRadius: 10,
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: EMZI_COLORS.textPrimary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {u.name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: EMZI_COLORS.textMuted,
                    marginTop: 2,
                    letterSpacing: 0.5,
                    fontWeight: 700,
                  }}
                >
                  {u.role}
                </div>
              </div>
              <DaysBadge days={u.days_inactive} tone={tone} />
              <ChevronRight size={14} color={EMZI_COLORS.textMuted} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const OrgAdoptionInactivePage = ({ mode = 'inactive' }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const cfg = MODES[mode] || MODES.inactive;
  const Icon = cfg.icon;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/org-adoption`, {
          headers: {
            Authorization: `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin, navigate]);

  // Group + filter
  const teams = useMemo(() => {
    if (!data) return [];
    const sourceList = data[cfg.field] || [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? sourceList.filter(
          (u) =>
            u.name.toLowerCase().includes(q) ||
            (u.role || '').toLowerCase().includes(q) ||
            (u.group || '').toLowerCase().includes(q),
        )
      : sourceList;
    const map = new Map();
    filtered.forEach((u) => {
      const key = u.group || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    });
    return Array.from(map.entries()).sort((a, b) => {
      if (b[1].length !== a[1].length) return b[1].length - a[1].length;
      return a[0].localeCompare(b[0]);
    });
  }, [data, query, cfg.field]);

  const totalShown = teams.reduce((sum, [, m]) => sum + m.length, 0);
  const totalInMode = data ? (data[cfg.field] || []).length : 0;

  return (
    <div
      data-testid="org-adoption-inactive-page"
      style={{ minHeight: '100vh', background: EMZI_COLORS.background, display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          background: cfg.headerBg,
          padding: '20px 16px 16px',
          color: 'white',
          boxShadow: EMZI_SHADOWS.md,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <button
            data-testid="inactive-back-btn"
            onClick={() => navigate(cfg.backTo)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: 8,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                fontSize: 20,
                fontWeight: 800,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon size={20} strokeWidth={2.4} />
              {cfg.title}
            </h1>
            {data && (
              <p style={{ fontSize: 12, opacity: 0.9, margin: '4px 0 0 0' }}>
                {totalInMode} user{totalInMode === 1 ? '' : 's'} · {teams.length} team
                {teams.length === 1 ? '' : 's'} · {cfg.subtitleSuffix}
              </p>
            )}
          </div>
        </div>

        {/* Search */}
        <div
          style={{
            background: 'rgba(255,255,255,0.92)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
          }}
        >
          <Search size={16} color={EMZI_COLORS.textMuted} />
          <input
            data-testid="inactive-search-input"
            type="text"
            placeholder="Search by name, role, or team…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: 13,
              background: 'transparent',
              color: EMZI_COLORS.textPrimary,
              fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              data-testid="inactive-search-clear"
              onClick={() => setQuery('')}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 12,
                color: EMZI_COLORS.textMuted,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          maxWidth: 720,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: EMZI_COLORS.textSecondary }}>
            Loading…
          </div>
        )}
        {error && (
          <div
            data-testid="inactive-error"
            style={{
              padding: 16,
              borderRadius: 12,
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              color: '#991B1B',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {!loading && !error && data && teams.length === 0 && (
          <div
            data-testid="inactive-empty"
            style={{
              padding: 24,
              textAlign: 'center',
              border: `1px dashed ${EMZI_COLORS.border}`,
              borderRadius: 12,
              color: EMZI_COLORS.textMuted,
              background: '#fff',
            }}
          >
            <Users size={28} style={{ margin: '0 auto 8px' }} />
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {query
                ? 'No matches'
                : mode === 'inactive'
                ? '🎉 No inactive users'
                : 'No users in this window'}
            </div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              {query
                ? 'Try a different search term.'
                : mode === 'inactive'
                ? 'Everyone has been active in the last 7 days.'
                : `No users found that match the ${mode.toUpperCase()} criteria.`}
            </div>
          </div>
        )}

        {!loading && !error && teams.length > 0 && (
          <>
            {query && (
              <div style={{ fontSize: 12, color: EMZI_COLORS.textSecondary, padding: '0 4px' }}>
                Showing {totalShown} match{totalShown === 1 ? '' : 'es'} across {teams.length} team
                {teams.length === 1 ? '' : 's'}.
              </div>
            )}

            {teams.map(([teamName, members], idx) => (
              <TeamSection
                key={teamName}
                team={teamName}
                members={members}
                totalMembers={null}
                defaultOpen={idx < 3}
                tone={cfg.badgeTone}
                getSeverity={cfg.barColors}
              />
            ))}
          </>
        )}

        {data && (
          <div
            style={{
              textAlign: 'right',
              fontSize: 10,
              color: EMZI_COLORS.textMuted,
              marginTop: 8,
            }}
          >
            Generated: {new Date(data.generated_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgAdoptionInactivePage;
